import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Truck, Pencil, Save, X, ChevronsUpDown, Search } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

export default function SupplierCompany() {
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editSel, setEditSel] = useState(new Set());
  const [search, setSearch] = useState("");

  const load = () => {
    api.get("/vendors").then(r => setVendors(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/companies").then(r => setCompanies(r.data)).catch(e => toast.error(formatErr(e)));
  };
  useEffect(() => { load(); }, []);

  const openEdit = (v) => { setEditId(v.id); setEditSel(new Set(v.company_ids || [])); setSearch(""); };
  const cancelEdit = () => { setEditId(null); setEditSel(new Set()); setSearch(""); };

  const toggle = (cid) => setEditSel(s => {
    const n = new Set(s);
    if (n.has(cid)) n.delete(cid); else n.add(cid);
    return n;
  });

  const save = async (vid) => {
    try {
      await api.patch(`/vendors/${vid}/companies`, { company_ids: [...editSel] });
      toast.success("Mapping saved");
      cancelEdit();
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const mappedNames = (v) => (v.company_ids || [])
    .map(cid => companies.find(c => c.id === cid)?.name)
    .filter(Boolean);

  const pg = usePagination(vendors, search);
  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Truck className="w-3 h-3"/> Inventory · Which supplier sells which company</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Supplier Wise Company</h1>
      </div>

      {companies.length === 0 && (
        <div className="border border-primary/40 bg-primary/5 p-4 rounded-sm text-sm text-muted-foreground mb-4">
          Add companies in <span className="text-primary font-semibold">Company Master</span> first, then map them to suppliers here.
        </div>
      )}

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="supplier-companies-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Supplier Name</th>
              <th className="text-left p-3">Mapped Companies</th>
              <th className="text-right p-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors.length === 0 && (
              <tr><td colSpan={3} className="p-8 text-center text-muted-foreground" data-testid="sc-empty">No suppliers yet — create vendors from a Purchase transaction or ask the owner to add them.</td></tr>
            )}
            {pg.pageItems.map(v => {
              const names = mappedNames(v);
              const editing = editId === v.id;
              return [
                <tr key={v.id} data-testid={`sc-row-${v.id}`} className={`border-t border-border ${editing ? "bg-primary/5" : "hover:bg-secondary/50"}`}>
                  <td className="p-3">
                    <div className="font-semibold">{v.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{v.phone}{v.is_wholesale ? " · WHOLESALE" : ""}</div>
                  </td>
                  <td className="p-3" data-testid={`sc-mapped-${v.id}`}>
                    {names.length ? (
                      <div className="flex flex-wrap gap-1">
                        {names.map(n => <span key={n} className="px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider border border-primary/40 bg-primary/10 text-primary">{n}</span>)}
                      </div>
                    ) : <span className="text-xs text-muted-foreground italic">No companies mapped</span>}
                  </td>
                  <td className="p-3 text-right">
                    <Button data-testid={`sc-edit-${v.id}`} size="sm" variant="ghost" onClick={() => editing ? cancelEdit() : openEdit(v)} className="h-7 px-2 text-xs uppercase tracking-wider">
                      <Pencil className="w-3 h-3 mr-1"/> {editing ? "Close" : "Edit"}
                    </Button>
                  </td>
                </tr>,
                editing && (
                  <tr key={`${v.id}-panel`} data-testid={`sc-panel-${v.id}`} className="border-t border-border bg-secondary/30">
                    <td colSpan={3} className="p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Companies supplied by <span className="text-primary font-semibold">{v.name}</span></div>
                      <div className="flex items-start gap-3 flex-wrap">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" data-testid={`sc-company-dropdown-${v.id}`} className="rounded-sm bg-secondary border-border min-w-[320px] max-w-[560px] justify-between h-auto min-h-9 py-1.5 font-normal">
                              <span className="flex flex-wrap gap-1">
                                {editSel.size === 0 && <span className="text-muted-foreground text-xs">Select companies…</span>}
                                {[...editSel].map(cid => {
                                  const c = companies.find(x => x.id === cid);
                                  return c ? <span key={cid} className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider border border-primary/40 bg-primary/10 text-primary">{c.name}</span> : null;
                                })}
                              </span>
                              <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground ml-2 shrink-0"/>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[320px] p-1 rounded-sm bg-card border-border" align="start">
                            <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border mb-1 sticky top-0 bg-card">
                              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0"/>
                              <input
                                data-testid={`sc-search-${v.id}`}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search companies…"
                                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                              />
                            </div>
                            <div className="max-h-56 overflow-y-auto">
                              {companies.length === 0 && <div className="text-xs text-muted-foreground italic p-2">No companies in Company Master</div>}
                              {companies.length > 0 && companies.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase())).length === 0 && (
                                <div className="text-xs text-muted-foreground italic p-2" data-testid={`sc-no-match-${v.id}`}>No companies match "{search}"</div>
                              )}
                              {companies.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase())).map(c => (
                                <label key={c.id} data-testid={`sc-opt-${v.id}-${c.id}`} className="flex items-center gap-2 text-sm cursor-pointer rounded-sm px-2 py-1.5 hover:bg-secondary transition-colors">
                                  <Checkbox data-testid={`sc-check-${v.id}-${c.id}`} checked={editSel.has(c.id)} onCheckedChange={() => toggle(c.id)}/>
                                  <span>{c.name}</span>
                                </label>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <div className="flex gap-2 ml-auto">
                          <Button data-testid={`sc-cancel-${v.id}`} variant="ghost" size="sm" onClick={cancelEdit} className="rounded-sm text-xs uppercase tracking-wider"><X className="w-3 h-3 mr-1"/> Cancel</Button>
                          <Button data-testid={`sc-save-${v.id}`} size="sm" onClick={() => save(v.id)} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-wider text-xs"><Save className="w-3 h-3 mr-1"/> Save</Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
